<template>
    <app-page>
        <content-container class="p-1">
            <h1 class="page-title">

                <div v-if="group" class="page-title-media">
                    <div class="page-title-media-object">
                        <group-avatar
                            :name="group.reportName"
                            :src="group.iconImage"
                            :value="group.iconText"
                            :color="group.iconTextColor"
                            :background-color="group.iconImage ? '#37474F' : group.iconBackgroundColor"
                            :transparent="false"
                            :size="40"
                        />
                        <div class="btn-edit-icon" @click="editGroupIcon">
                            <icon src="edit" />
                            <tooltip title="Edit icon" />
                        </div>

                    </div>
                    <div class="page-title-media-body">
                        {{group.reportName}}
                    </div>
                </div>

                <template v-else>
                    Manage group
                </template>

            </h1>

            <toolbar class="pb-1" theme="light">
                <toolbar-group>
                    <toolbar-button
                        icon="keyboard_backspace"
                        title="Back to groups"
                        @click.prevent="goBack"
                    />
                </toolbar-group>

                <toolbar-group>
                    <toolbar-button
                        icon="delete"
                        title="Delete group"
                        @click.prevent="deleteGroup"
                    />
                </toolbar-group>
            </toolbar>

            <tab-group :sync-route="true" theme="card-folders">
                <tab :to="{ name: 'manage-group-users', params: { locationGroupId } }" label="Users">
                    <card title="Users">
                        <toolbar class="p-1">
                            <toolbar-group>
                                <toolbar-button
                                    :title="addUserTitle"
                                    icon="person_add"
                                    @click="addUser"
                                />
                            </toolbar-group>
                        </toolbar>

                        <section>
                            <user-list-grid
                                ref="userListGrid"
                                :location-group-id="locationGroupId"
                                @change="onUserListGridChange"
                            />
                        </section>

                        <add-registered-user-modal
                            ref="addRegisteredUserModal"
                            :location-group-id="locationGroupId"
                            @add="onUserAdded"
                        />
                    </card>
                </tab>

                <tab :to="{ name: 'manage-group-dashboards', params: { locationGroupId } }" label="Dashboards">
                    <card title="Dashboards">
                        <section class="pt-1">

                            <group-dashboards-grid
                                :location-group-id="locationGroupId"
                            />

                        </section>
                    </card>
                </tab>

            </tab-group>

        </content-container>

        <edit-group-icon-modal
            ref="editGroupIconModal"
            :location-group-id="locationGroupId"
            @save="onAvatarSave"
        />

        <delete-group-modal
            ref="deleteGroupModal"
            :location-group-id="locationGroupId"
            @delete="onGroupDeleted"
        />

    </app-page>
</template>


<script>
    import _ from 'lodash';
    import { AsyncMixin, FormValidationMixin } from 'pdk/mixins';
    import { notifications } from 'pdk/services';
    import { actions as dashboardActions } from 'dashboard-pdk/store';
    import store, { actions as appActions} from '@/store';
    import { UserListGrid } from '@/components/users';
    import { GroupAvatar, GroupDashboardsGrid } from '@/components/groups';
    import { groups } from '@/services';
    import { AddRegisteredUserModal, DeleteGroupModal, EditGroupIconModal } from './modals';


    export default {
        name: 'group-editor',
        components: {
            AddRegisteredUserModal,
            DeleteGroupModal,
            EditGroupIconModal,
            GroupAvatar,
            GroupDashboardsGrid,
            UserListGrid
        },
        mixins: [AsyncMixin, FormValidationMixin],        
        beforeRouteEnter(to, from, next) {
            groups.getGroup({
                locationGroupID: to.params.locationGroupId
            }).then((group) => {
                next((vm) => {
                    vm.group = group;
                });
            }).catch((e) => {
                next({ name: '404', replace: true });
            });
        },
        beforeRouteUpdate(to, from, next) {
            this.refreshGroup(to.params.locationGroupId)
                .then(() => {
                    next();
                })
                .catch((e) => {
                    next({ name: '404', replace: true });
                });
        },
        props: {
            locationGroupId: {
                type: String,
                required: true
            }
        },
        data() {
            return {
                group: undefined
            };
        },
        computed: {
            addUserTitle() {
                if (!this.group || !this.group.reportName)
                    return 'Add user to group';

                return `Add user to ${this.group.reportName}`;
            }
        },
        methods: {
            refreshGroup(id) {
                return this.async(groups.getGroup({
                    locationGroupID: id || this.locationGroupId
                }))
                .then((group) => {
                    this.group = group;
                    return group;
                });
            },
            addUser() {
                this.$refs.addRegisteredUserModal.open();
            },
            editGroupIcon() {
                this.$refs.editGroupIconModal.open();
            },
            deleteGroup() {
                this.$refs.deleteGroupModal.openWith(this.locationGroupId);
            },

            onUserListGridChange(user) {
                this.$router.push({
                    name: 'manage-group-user',
                    params: {
                        locationGroupId: this.locationGroupId,
                        globalUserId: user.globalUserID
                    }
                });
            },
            onUserAdded() {
                this.$refs.userListGrid.refresh();
            },
            onGroupDeleted(e) {
                store.dispatch(dashboardActions.ACTIVE_LOCATION_GROUP_ID_CLEAR);
                store.dispatch(dashboardActions.LOCATION_GROUPS_LOAD);
                store.dispatch(appActions.USER_GROUP_ROLES_LOAD);

                notifications.open({
                    content: `"${e.group.reportName}" group deleted`
                });

                this.$router.push({ name: 'group-list', replace: true });
            },
            onAvatarSave() {
                this.refreshGroup();
            },

            goBack() {
                this.$router.go(-1);
            }
        }
    };
</script>


<style lang="scss">
@import '~@/assets/styles/references';

.page-title-media { display: flex; justify-content: center; align-items: center; }
.page-title-media-object { flex: 0 0 auto; position: relative;
    .btn-edit-icon { position: absolute; z-index: 1; top: 0; left: 0; display: flex; justify-content: center; align-items: center; width: 40px; height: 40px; border-radius: 20px; background-color: rgba(0, 0, 0, 0.5); opacity: 0; cursor: pointer;
        &:hover { opacity: 1; }
        .icon { color: color(white); }    
    }
}
.page-title-media-body { flex: 1 1 auto; padding: 0 gx(1); }

</style>
